import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { Content, bootstrap } from '../generated/content';
import { createTranslator, Language, Translate, validateContent } from '../utils/content';

interface ContentContextType {
  content: Content;
  language: Language;
  toggleLanguage: () => void;
  t: Translate;
}

const ContentContext = createContext<ContentContextType | null>(null);

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) throw new Error('useContent must be used within a ContentProvider');
  return context;
};

export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<Content | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('app_language');
      if (saved === 'en' || saved === 'jp') return saved;
    } catch {}
    return navigator.language.toLowerCase().startsWith('ja') ? 'jp' : 'en';
  });

  useEffect(() => {
    document.documentElement.lang = language === 'jp' ? 'ja' : 'en';
    try { localStorage.setItem('app_language', language); } catch {}
  }, [language]);

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    fetch(`/content.json?v=${Date.now()}`, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`Content HTTP ${res.status}`);
        return res.json();
      })
      .then(data => { validateContent(data); if (!controller.signal.aborted) setContent(data); })
      .catch(error => {
        if (!controller.signal.aborted) { console.error('Failed to fetch content.json:', error); setFailed(true); }
      });
    return () => controller.abort();
  }, [attempt]);

  const t = useMemo(() => content ? createTranslator(content, language) : null, [content, language]);
  if (!content || !t) {
    const labels = bootstrap[language];
    return <main className="p-6">
      <p role={failed ? 'alert' : 'status'}>{failed ? labels.contentError : labels.loading}</p>
      {failed && <button type="button" className="mt-4 rounded border px-4 py-3" onClick={() => setAttempt(value => value + 1)}>{labels.retry}</button>}
    </main>;
  }

  return <ContentContext.Provider value={{ content, language, t, toggleLanguage: () => setLanguage(prev => prev === 'en' ? 'jp' : 'en') }}>
    {children}
  </ContentContext.Provider>;
};
